import { useState } from 'react'
import { PlusLg, CheckCircleFill, Send, ChevronLeft, ChevronRight, ExclamationTriangle } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Stat, FormModal, RowActions, Amount } from '../components/ui.jsx'
import { entries, recurringForMonth, isRecurringActive } from '../lib/finance.js'
import { money, monthLabel, dateLabel, todayISO } from '../lib/format.js'
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES } from '../lib/constants.js'
import { add, setAt, removeAt } from '../lib/firebase.js'

const KINDS = [{ id: 'bill', label: 'Bill' }, { id: 'subscription', label: 'Subscription' }, { id: 'income', label: 'Income' }, { id: 'other', label: 'Other' }]

const fields = (accounts) => [
  { name: 'name', label: 'Name', required: true },
  { name: 'type', label: 'Direction', type: 'select', required: true, col: 'col-6', options: [{ id: 'expense', label: 'Outcome' }, { id: 'income', label: 'Income' }] },
  { name: 'kind', label: 'Kind', type: 'select', required: true, col: 'col-6', options: KINDS },
  { name: 'amount', label: 'Amount', type: 'number', step: '0.01', col: 'col-6', help: 'Leave empty if it varies (e.g. utilities).' },
  { name: 'dayOfMonth', label: 'Day of month', type: 'number', required: true, col: 'col-6', step: '1' },
  { name: 'category', label: 'Category', type: 'select', required: true, col: 'col-6', options: (v) => (v.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES) },
  { name: 'accountId', label: 'Account', type: 'select', col: 'col-6', options: accounts.map((a) => ({ id: a.id, label: a.name })) },
  { name: 'startDate', label: 'Starts', type: 'date', col: 'col-6' },
  { name: 'endDate', label: 'Ends', type: 'date', col: 'col-6' },
  { name: 'cancel', label: 'Planning to cancel', type: 'checkbox', col: 'col-6' },
  { name: 'paused', label: 'Paused', type: 'checkbox', col: 'col-6' },
]

const shiftMonth = (m, n) => { const [y, mo] = m.split('-').map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }

export default function Recurring() {
  const data = useData()
  const [month, setMonth] = useState(todayISO().slice(0, 7))
  const [edit, setEdit] = useState(null)
  const [posting, setPosting] = useState(null)
  const accounts = entries(data.accounts)
  const items = recurringForMonth(data, month)
  const all = entries(data.recurring).sort((a, b) => (a.dayOfMonth || 0) - (b.dayOfMonth || 0))
  const today = todayISO()
  const active = all.filter((r) => isRecurringActive(r, today))
  const fixedIn = active.filter((r) => r.type === 'income').reduce((s, r) => s + (r.amount || 0), 0)
  const fixedOut = active.filter((r) => r.type === 'expense').reduce((s, r) => s + (r.amount || 0), 0)
  const subs = active.filter((r) => r.kind === 'subscription').reduce((s, r) => s + (r.amount || 0), 0)

  async function save(values) {
    const { id, due, posted, ...rest } = edit // eslint-disable-line no-unused-vars
    const rec = { ...rest, ...values }
    if (id) await setAt(`recurring/${id}`, rec)
    else await add('recurring', rec)
  }

  async function post(r, amount, date) {
    await add('transactions', {
      type: r.type, amount: Math.abs(amount), date, category: r.category, note: r.name,
      accountId: r.accountId || null, recurringId: r.id, createdAt: Date.now(),
    })
  }

  const postAllFixed = () => Promise.all(items.filter((r) => !r.posted && r.amount != null && r.due <= today).map((r) => post(r, r.amount, r.due)))
  const pendingFixed = items.filter((r) => !r.posted && r.amount != null && r.due <= today).length

  return (
    <>
      <PageHeader title="Bills & recurring" subtitle="Salary, loan payment, bills and subscriptions. Post them to create the month's transactions.">
        <button className="btn btn-primary" onClick={() => setEdit({ type: 'expense', kind: 'bill', category: 'Utilities', dayOfMonth: 1 })}><PlusLg /> Recurring item</button>
      </PageHeader>

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3"><Stat label="Fixed income / month" value={money(fixedIn)} tone="pos" /></div>
        <div className="col-6 col-lg-3"><Stat label="Fixed outcome / month" value={money(fixedOut)} tone="neg" /></div>
        <div className="col-6 col-lg-3"><Stat label="Left after fixed" value={money(fixedIn - fixedOut, { sign: true })} /></div>
        <div className="col-6 col-lg-3"><Stat label="Subscriptions" value={money(subs)} hint={`${active.filter((r) => r.kind === 'subscription').length} active`} /></div>
      </div>

      <Panel className="mb-3" title={
        <span className="d-flex align-items-center gap-2">
          <button className="icon-btn" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft /></button>
          {monthLabel(month)}
          <button className="icon-btn" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight /></button>
        </span>}
        action={pendingFixed > 0 && <button className="btn btn-sm btn-outline-primary" onClick={postAllFixed}><Send /> Post {pendingFixed} due item{pendingFixed > 1 ? 's' : ''}</button>}>
        {items.length ? (
          <div className="table-wrap"><table className="table align-middle">
            <thead><tr><th>Due</th><th>Name</th><th>Kind</th><th className="text-end">Amount</th><th className="text-end">Status</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="small text-nowrap">{dateLabel(r.due)}</td>
                  <td>{r.name} {r.cancel && <span className="badge text-bg-warning ms-1"><ExclamationTriangle /> cancel</span>}</td>
                  <td className="small text-body-secondary">{KINDS.find((k) => k.id === r.kind)?.label}</td>
                  <td className="num">{r.amount != null ? <Amount value={r.amount} type={r.type} /> : <span className="text-body-secondary">varies</span>}</td>
                  <td className="text-end">
                    {r.posted ? <span className="text-success small"><CheckCircleFill /> posted</span>
                      : <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setPosting(r)}>Post</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <div className="empty">No recurring items this month.</div>}
      </Panel>

      <Panel title="All recurring items">
        {all.length ? (
          <div className="table-wrap"><table className="table table-sm align-middle">
            <thead><tr><th>Day</th><th>Name</th><th>Category</th><th>Ends</th><th className="text-end">Amount</th><th /></tr></thead>
            <tbody>
              {all.map((r) => (
                <tr key={r.id} className={isRecurringActive(r, today) ? '' : 'opacity-50'}>
                  <td className="small">{r.dayOfMonth}.</td>
                  <td>{r.name}{r.paused && <span className="text-body-secondary small"> · paused</span>}{r.cancel && <span className="badge text-bg-warning ms-1">cancel</span>}</td>
                  <td className="small">{r.category}</td>
                  <td className="small text-body-secondary">{r.endDate ? dateLabel(r.endDate) : ''}</td>
                  <td className="num">{r.amount != null ? <Amount value={r.amount} type={r.type} /> : 'varies'}</td>
                  <td className="text-end"><RowActions onEdit={() => setEdit(r)} onDelete={() => removeAt(`recurring/${r.id}`)} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <div className="empty">Nothing yet.</div>}
      </Panel>

      {edit && <FormModal title={edit.id ? 'Edit recurring item' : 'New recurring item'} fields={fields(accounts)} initial={edit} onSubmit={save} onClose={() => setEdit(null)} />}
      {posting && <FormModal title={`Post: ${posting.name}`} submitLabel="Post" initial={{ amount: posting.amount, date: posting.due }}
        fields={[{ name: 'amount', label: 'Amount', type: 'number', required: true, step: '0.01', col: 'col-6' }, { name: 'date', label: 'Date', type: 'date', required: true, col: 'col-6' }]}
        onSubmit={(v) => post(posting, v.amount, v.date)} onClose={() => setPosting(null)} />}
    </>
  )
}
